import { BRAND_CONFIG } from '@@brand'
import { GQL_ID, Product, Shop } from '@@types'
import { Map, Placemark, YMaps, ZoomControl } from '@pbe/react-yandex-maps'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'usehooks-ts'

import { useFetchShopsWithProductInStock } from '@entities/Product'

import TabsSwitcher from '@widgets/modal-delivery/ui/TabsSwitcher'

import useShopTime, { formatTimeFromSeconds } from '@shared/hooks/useShopTime'
import { cn } from '@shared/lib/cn'
import Input from '@shared/ui/input'

import styles from './ShopsWithProduct.module.scss'

interface ShopsWithProductProps {
  product: Product
}

function formatAmount (amount: number, product: Product): string {
  if (product.price_type === 'weight') {
    if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)} кг`
    }
    return `${amount} г`
  }
  return `${amount} шт`
}

interface ShopListItemProps {
  item: { shop_id: GQL_ID; amount: number; shop: Pick<Shop, 'id' | 'name' | 'address' | 'shopGeoPoint' | 'open_time' | 'close_time' | 'distance'> }
  product: Product
  isSelected: boolean
  onSelect: (shopId: GQL_ID, lat?: number, lon?: number) => void
  t: (key: string, options?: Record<string, unknown>) => string
}

function ShopListItem ({ item, product, isSelected, onSelect, t }: ShopListItemProps) {
  const { open, close, getShopStatus } = useShopTime(
    item.shop?.open_time || 0,
    item.shop?.close_time || 0,
  )
  const isLowStock = item.amount <= 5

  return (
    <div
      className={cn(styles.shop, {
        [styles['shop--selected']]: isSelected
      })}
      onClick={() => onSelect(
        item.shop_id,
        item.shop?.shopGeoPoint?.latitude,
        item.shop?.shopGeoPoint?.longitude
      )}
    >
      <div className={styles.shop__row}>
        <span className={styles.shop__address}>{item.shop?.address}</span>
        <span
          className={cn(styles.shop__amount, {
            [styles['shop__amount--low']]: isLowStock,
            [styles['shop__amount--high']]: !isLowStock
          })}
        >
          {formatAmount(item.amount, product)}
        </span>
      </div>
      <div className={styles.shop__status}>
        {getShopStatus ? (
          <span className={styles.shop__time}>
            {t('shops.open_hours', { open, close })}
          </span>
        ) : (
          <>
            <span className={styles.shop__closed}>{t('shops.closed')}</span>
            <span className={styles.shop__separator} />
            <span className={styles.shop__time}>{t('shops.opens_at', { time: open })}</span>
          </>
        )}
      </div>
    </div>
  )
}

export default function ShopsWithProduct ({ product }: ShopsWithProductProps) {
  const { t } = useTranslation('product')
  const [ filterText, setFilterText ] = useState('')
  const [ viewMode, setViewMode ] = useState<'list' | 'map'>('map')
  const [ selectedShopId, setSelectedShopId ] = useState<GQL_ID | null>(null)
  const isMobile = useMediaQuery('(max-width: 768px)')
  const mapRef = useRef<ymaps.Map | null>(null)

  const { shops, loading, error } = useFetchShopsWithProductInStock({
    productId: product.id
  })

  const handleShopSelect = useCallback((shopId: GQL_ID, lat?: number, lon?: number) => {
    setSelectedShopId(shopId)
    if (lat && lon && mapRef.current) {
      mapRef.current.setCenter([ lat, lon ], 16, { duration: 300 })
    }
  }, [])

  const filteredShops = useMemo(() => shops
    .filter(shop => 
      shop.shop?.address?.toLowerCase().includes(filterText.toLowerCase()) || 
      shop.shop?.name?.toLowerCase().includes(filterText.toLowerCase())
    )
    .sort((a, b) => {
      const distA = parseFloat(a.shop?.distance?.replace(/[^\d.]/g, '') || '999999')
      const distB = parseFloat(b.shop?.distance?.replace(/[^\d.]/g, '') || '999999')
      return distA - distB
    }), [ shops, filterText ])

  const mapCenter = useMemo<[number, number]>(() => {
    if (shops.length > 0 && shops[0].shop?.shopGeoPoint) {
      return [ shops[0].shop.shopGeoPoint.latitude, shops[0].shop.shopGeoPoint.longitude ]
    }
    return [ 50.300377, 57.154555 ] // Default center
  }, [ shops ])

  if (loading) {
    return (
      <div className={styles.root}>
        <div className={styles.root__loading}>
          {t('shops.loading')}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.root}>
        <div className={styles.root__error}>
          {t('shops.error')}
        </div>
      </div>
    )
  }

  if (!shops || shops.length === 0) {
    return (
      <div className={styles.root}>
        <div className={styles.root__empty}>
          {t('shops.empty')}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.root}>
      {/* Mobile: показываем switcher */}
      {isMobile && (
        <div className={styles.root__switcher}>
          <TabsSwitcher
            tabs={[
              { label: t('shops.view_map'), value: 'map' },
              { label: t('shops.view_list'), value: 'list' }
            ]}
            active={viewMode}
            onChange={(v) => setViewMode(v as 'list' | 'map')}
          />
        </div>
      )}

      {/* Desktop: карта + список рядом */}
      {!isMobile && (
        <div className={styles.root__desktop}>
          <div className={styles.root__sidebar}>
            <div className={styles.root__search}>
              <Input
                placeholder={t('shops.search_placeholder')}
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </div>
            <div className={styles.root__list}>
              {filteredShops.map((item) => (
                <ShopListItem
                  key={item.shop_id}
                  item={item}
                  product={product}
                  isSelected={selectedShopId === item.shop_id}
                  onSelect={handleShopSelect}
                  t={t}
                />
              ))}
            </div>
          </div>
          <div className={styles.root__map}>
            <YMaps
              query={{
                apikey: import.meta.env.VITE_YANDEX_MAPS_API_KEY,
                load: 'package.full',
              }}
            >
              <Map
                defaultState={{
                  center: mapCenter,
                  zoom: 12,
                }}
                width="100%"
                height="100%"
                instanceRef={(ref) => {
                  if (ref) mapRef.current = ref
                }}
                options={{
                  suppressMapOpenBlock: true,
                  copyrightLogoVisible: false,
                  copyrightUaVisible: false,
                }}
              >
                <ZoomControl options={{ position: { right: 16, bottom: 80 } }} />
                {shops.map((item) => {
                  const lat = item.shop?.shopGeoPoint?.latitude
                  const lon = item.shop?.shopGeoPoint?.longitude
                  if (!lat || !lon) return null

                  return (
                    <Placemark
                      key={item.shop_id}
                      geometry={[ lat, lon ]}
                      properties={{
                        balloonContentHeader: item.shop?.name || item.shop?.address,
                        balloonContentBody: `${formatAmount(item.amount, product)} • ${formatTimeFromSeconds(item.shop?.open_time || 0)} - ${formatTimeFromSeconds(item.shop?.close_time || 0)}`,
                      }}
                      options={{
                        iconLayout: 'default#image',
                        iconImageHref: BRAND_CONFIG.images.mapPin,
                        iconImageSize: [ 36, 55 ],
                        iconImageOffset: [ -18, -55 ],
                      }}
                    />
                  )
                })}
              </Map>
            </YMaps>
          </div>
        </div>
      )}

      {/* Mobile: list view */}
      {isMobile && viewMode === 'list' && (
        <>
          <div className={styles.root__search}>
            <Input
              placeholder={t('shops.search_placeholder')}
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </div>

          <div className={styles.root__list}>
            {filteredShops.map((item) => (
              <ShopListItem
                key={item.shop_id}
                item={item}
                product={product}
                isSelected={false}
                onSelect={() => {}}
                t={t}
              />
            ))}
          </div>

          {filteredShops.length === 0 && filterText && (
            <div className={styles.root__empty}>
              {t('shops.not_found')}
            </div>
          )}
        </>
      )}

      {/* Mobile: map view */}
      {isMobile && viewMode === 'map' && (
        <div className={styles.root__map}>
          <YMaps
            query={{
              apikey: import.meta.env.VITE_YANDEX_MAPS_API_KEY,
              load: 'package.full',
            }}
          >
            <Map
              defaultState={{
                center: mapCenter,
                zoom: 12,
              }}
              width="100%"
              height="100%"
              options={{
                suppressMapOpenBlock: true,
                copyrightLogoVisible: false,
                copyrightUaVisible: false,
              }}
            >
              <ZoomControl options={{ position: { right: 16, bottom: 16 } }} />
              {shops.map((item) => {
                const lat = item.shop?.shopGeoPoint?.latitude
                const lon = item.shop?.shopGeoPoint?.longitude
                if (!lat || !lon) return null

                return (
                  <Placemark
                    key={item.shop_id}
                    geometry={[ lat, lon ]}
                    properties={{
                      balloonContentHeader: item.shop?.name || item.shop?.address,
                      balloonContentBody: `${formatAmount(item.amount, product)} • ${formatTimeFromSeconds(item.shop?.open_time || 0)} - ${formatTimeFromSeconds(item.shop?.close_time || 0)}`,
                    }}
                    options={{
                      iconLayout: 'default#image',
                      iconImageHref: BRAND_CONFIG.images.mapPin,
                      iconImageSize: [ 36, 55 ],
                      iconImageOffset: [ -18, -55 ],
                    }}
                  />
                )
              })}
            </Map>
          </YMaps>
        </div>
      )}
    </div>
  )
}
